import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpInternalService } from './http.service';
import {
  ReportAssignmentResponse,
  TransactionAccountSummary,
  TransactionLedgerFilters,
  TransactionLedgerReportScope,
  TransactionLedgerResponse,
  TransactionSortColumn,
  TransactionSortDirection,
  TransactionStateFilter
} from '../model/transaction-ledger';

/**
 * Transactions view: the bank/card ledger and the report filing actions on it.
 *
 * Talks to the api/transactions controller, which uses a literal route rather than
 * the older api/[controller]/[action] convention -- hence the lowercase paths.
 *
 * Paths here omit the leading "api/" because HttpInternalService prepends
 * AppConfigService.apiURL, which is already "/api/".
 */
@Injectable({
  providedIn: 'root'
})
export class TransactionsService {

  /**
   * Local date as yyyy-MM-dd. Deliberately not toISOString(), which converts to UTC
   * and can move a date across a day boundary for anyone east or west of Greenwich --
   * picking "1 Sep" in Sydney would otherwise query from 31 Aug.
   */
  private static toIsoDate(value: Date): string {
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }

  constructor(private _httpService: HttpInternalService) { }

  /**
   * One page of an account's statement. `from` / `to` are dates, not timestamps: the
   * server widens `to` to the end of that day so "to 30 Sep" includes 30 Sep.
   *
   * The sort is the server's, not the client's: only one page is loaded at a time, so
   * sorting here would order the rows on screen and leave the rest of the statement
   * where it was.
   *
   * `filters` is the Transactions screen's filter panel; every part of it is optional and is
   * left off the query when empty, so the report view and an unfiltered screen send what they
   * always have.
   */
  getLedger(
    organisationId: string,
    accountId: string | null,
    from: Date | null,
    to: Date | null,
    states: TransactionStateFilter,
    search: string,
    page: number = 1,
    pageSize: number = 100,
    sortBy: TransactionSortColumn = 'date',
    sortDir: TransactionSortDirection = 'asc',
    reportScope: TransactionLedgerReportScope | null = null,
    filters: TransactionLedgerFilters | null = null
  ): Observable<TransactionLedgerResponse> {

    const selected: string[] = [];
    if (states.toReconcile) { selected.push('toReconcile'); }
    if (states.reconciled) { selected.push('reconciled'); }

    const params: any = {
      organisationId,
      // Neither ticked would read as an empty account rather than an over-tight
      // filter, so the server falls back to toReconcile. Send it explicitly.
      states: selected.length ? selected.join(',') : 'toReconcile',
      page: String(page),
      pageSize: String(pageSize),
      sortBy,
      sortDir
    };

    // Left out entirely for "All accounts": the API reads a missing account as every account
    // in the organisation.
    if (accountId) { params.accountId = accountId; }

    if (from) { params.from = TransactionsService.toIsoDate(from); }
    if (to) { params.to = TransactionsService.toIsoDate(to); }
    if (search && search.trim()) { params.search = search.trim(); }

    // SD-1227: one report's rows, for its coding view. The server checks the report is the
    // organisation's and narrows a Submitter to their own rows whatever userId says.
    if (reportScope && reportScope.reportId) {
      params.reportId = reportScope.reportId;
      if (reportScope.userId) { params.userId = reportScope.userId; }
    }

    if (filters) {
      if (filters.accountIds.length) { params.accountIds = filters.accountIds.join(','); }
      if (filters.txn) { params.txn = filters.txn; }
      if (filters.codedTo.length) { params.codedTo = filters.codedTo.join(','); }
      if (filters.details) { params.details = filters.details; }
      if (filters.report) { params.report = filters.report; }
      if (filters.reportIds?.length) { params.reportIds = filters.reportIds.join(','); }
      if (filters.notInReport) { params.notInReport = 'true'; }
      if (filters.amountMin !== null) { params.amountMin = String(filters.amountMin); }
      if (filters.amountMax !== null) { params.amountMax = String(filters.amountMax); }
    }

    return this._httpService.getRequest<TransactionLedgerResponse>('transactions', params);
  }

  /** The account switcher feed: every bank and card account with its pending count. */
  getAccounts(organisationId: string): Observable<TransactionAccountSummary[]> {
    return this._httpService.getRequest<TransactionAccountSummary[]>(
      'transactions/accounts', { organisationId });
  }

  /**
   * The reviewed Refresh, step one: what Xero has that is not here, and every status Xero
   * disagrees with. Writes nothing.
   */
  refreshPreview(organisationId: string): Observable<XeroRefreshPreview> {
    return this._httpService
      .postRequest<any>(`Reporting/RefreshPreview?organizationId=${organisationId}`, {})
      .pipe(map(response => {
        if (!response || response.status !== 1) {
          throw new Error((response && response.message) || 'We could not check Xero for changes.');
        }
        const data = response.data || {};
        return {
          accounts: data.accounts || [],
          newTransactions: data.newTransactions || [],
          statusChanges: data.statusChanges || []
        };
      }));
  }

  /** The reviewed Refresh, step two: imports the ticked new transactions and makes the ticked changes, as one run. */
  refreshApply(request: XeroRefreshApplyRequest): Observable<XeroRefreshApplyResult> {
    return this._httpService
      .postRequest<any>('Reporting/RefreshApply', request)
      .pipe(map(response => {
        if (!response || response.status !== 1) {
          throw new Error((response && response.message) || 'We could not apply the refresh.');
        }
        return response.data as XeroRefreshApplyResult;
      }));
  }

  /**
   * A Xero organisation's Refresh: marks the transactions already here that Xero has since
   * reconciled, and answers how many changed. It imports nothing. This is the older
   * api/Reporting controller, hence the different path style.
   */
  refreshFromXero(organisationId: string): Observable<{ checkedCount: number; updatedCount: number }> {
    return this._httpService
      .postRequest<any>(`Reporting/RefreshBankTransactions?organizationId=${organisationId}`, {})
      .pipe(map(response => ({
        checkedCount: (response && response.data && response.data.checkedCount) || 0,
        updatedCount: (response && response.data && response.data.updatedCount) || 0
      })));
  }

  /** What a Xero refresh did, as the toast says it. */
  static describeXeroRefresh(updatedCount: number): string {
    if (!updatedCount) { return 'No transactions have been reconciled since the last refresh.'; }
    return updatedCount === 1
      ? '1 transaction is now reconciled in Xero.'
      : `${updatedCount} transactions are now reconciled in Xero.`;
  }

  /**
   * Files a selection into a report. Sends no email -- unlike
   * Reporting/sendAssignedNotification, which is the paperwork request flow.
   */
  addToReport(organisationId: string, reportId: string, transactionIds: string[]):
    Observable<ReportAssignmentResponse> {

    return this._httpService.postRequest<ReportAssignmentResponse>(
      'transactions/report', { organisationId, reportId, transactionIds });
  }

  /** Gives a selection to one person in the organisation (not offered to Submitters; the server checks). */
  assignToUser(organisationId: string, userId: string, transactionIds: string[]):
    Observable<{ assignedCount: number; transactionIds: string[] }> {

    return this._httpService.postRequest<{ assignedCount: number; transactionIds: string[] }>(
      'transactions/assign', { organisationId, userId, transactionIds });
  }

  /** The organisation's expense reports, newest first, for the Expense report filter. */
  getReports(organisationId: string): Observable<{ id: string; name: string; createdAt: string; complete: boolean }[]> {
    return this._httpService.getRequest<any[]>('transactions/reports', { organisationId });
  }

  /** Whether "Suggest coding" is offered: only while the organisation's Coding Assistant is on. */
  suggestCodingAvailable(organisationId: string): Observable<boolean> {
    return this._httpService
      .getRequest<{ available: boolean }>('transactions/suggest-coding', { organisationId })
      .pipe(map(response => !!response?.available));
  }

  /**
   * The Coding Assistant suggests coding for a selection now. Matched and reconciled rows are
   * skipped; the suggestions come back on the rows as `agentCoding`, for a person to confirm.
   */
  suggestCoding(organisationId: string, transactionIds: string[]):
    Observable<{ message: string; eligibleCount: number; suggestedCount: number; clearedCount?: number; transactionIds: string[] }> {

    return this._httpService.postRequest<any>('transactions/suggest-coding', { organisationId, transactionIds });
  }

  /** Clears the report on a selection, leaving the transactions in the ledger. */
  removeFromReport(organisationId: string, transactionIds: string[]):
    Observable<ReportAssignmentResponse> {

    return this._httpService.postRequest<ReportAssignmentResponse>(
      'transactions/report/remove', { organisationId, transactionIds });
  }
}

/** One transaction already here whose status Xero disagrees with (api/Reporting/RefreshPreview). */
export interface XeroRefreshStatusChange {
  unreconciledReportId: string;
  /** "Reconciled" | "Unreconciled" | "Missing". */
  change: string;
  accountName: string;
  /** yyyy-MM-dd. */
  transactionDate: string;
  description: string;
  amount: number;
  /** False when Synced's own work on the row stops the change; it is shown with the reason. */
  canApply: boolean;
  reason: string | null;
}

export interface XeroRefreshPreview {
  accounts: { accountId: string; accountName: string; from: string; to: string }[];
  /** Xero statement lines, shaped as the Xero import's preview rows. */
  newTransactions: any[];
  statusChanges: XeroRefreshStatusChange[];
}

export interface XeroRefreshApplyRequest {
  organizationId: string;
  reportId: string | null;
  newTransactions: any[];
  changes: { unreconciledReportId: string; change: string }[];
}

export interface XeroRefreshApplyResult {
  insertedCount: number;
  droppedCount: number;
  reconciledCount: number;
  unreconciledCount: number;
  removedCount: number;
  skippedCount: number;
}
